import { Card, Container, Icon, Typography } from '@design-system';
import { BsBuildingsFill } from 'react-icons/bs';
import './WorksiteRecap.scss';
import { MdFactory } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

type WorksiteInfo = {
    id: string;
    name: string;
    operator_name: string;
    apartments_count: string;
};

type ConsumptionData = {
    consumed: number;
    budget: number;
    tasks: Array<{
        label: string;
        consumed: number;
        budget: number;
    }>;
    total: number;
};

export type WorksiteRecapProps = {
    worksite: WorksiteInfo;
    consumption: ConsumptionData;
    global: 'positive' | 'negative';
    id: string;
};

export const WorksiteRecap: React.FC<WorksiteRecapProps> = ({ worksite, consumption, global, id }) => {
    const navigate = useNavigate();

    const consumedHours = consumption.consumed;
    const budgetHours = consumption.budget;
    const difference = Math.round(budgetHours - consumedHours);
    const percentConsumed = budgetHours > 0 ? Math.round((consumedHours / budgetHours) * 100) : 0;

    const apartmentsCount = parseInt(worksite.apartments_count);
    const shouldUseMdFactory = apartmentsCount > 50;

    return (
        <Card className="worksite-recap" onClick={() => navigate(`/mes-chantiers/worksite-results?worksiteId=${id}`)}>
            <Container className="worksite-recap-container">
                <div className="worksite-recap-header">
                    <div>
                        <Typography intrinsic="h3" className="mb-x-small-spacing">
                            {worksite.name}
                        </Typography>
                        <Typography intrinsic="h5" color="grey">
                            {worksite.operator_name} • {worksite.apartments_count} logements
                        </Typography>
                    </div>
                    <Icon icon={shouldUseMdFactory ? <MdFactory /> : <BsBuildingsFill />} size={20} color="green" variant="surrounded" />
                </div>

                <Typography intrinsic="h4" className="mb-small-medium-spacing">
                    Bilan global
                </Typography>
                <Container className="bilan-global" padding="none">
                    <Container padding="none" className="bilan-global-item">
                        <Typography intrinsic="span" color="grey" className="mb-small-medium-spacing">
                            Consommation
                        </Typography>
                        <div className="bilan-global-item">
                            <Typography intrinsic="h5">{consumedHours}h</Typography>
                            <RecapItem amount={percentConsumed.toString()} unit="%" ratio={global} />
                        </div>
                    </Container>
                    <Container padding="none" className="bilan-global-item">
                        <Typography intrinsic="span" color="grey" className="mb-small-medium-spacing">
                            Budget
                        </Typography>
                        <div className="bilan-global-item">
                            <Typography intrinsic="h5">{budgetHours}h</Typography>
                        </div>
                    </Container>
                    <Container padding="none" className="bilan-global-item">
                        <Typography intrinsic="span" color="grey" className="mb-small-medium-spacing">
                            Différence
                        </Typography>
                        <div className="bilan-global-item">
                            <RecapItem amount={difference > 0 ? `+${difference}` : difference.toString()} unit="h" ratio={global} />
                        </div>
                    </Container>
                </Container>
                <Container className="bilan-global-items" padding="none"></Container>
            </Container>
        </Card>
    );
};

type RecapItemProps = {
    amount: string;
    unit: string;
    ratio: 'positive' | 'negative' | 'neutral';
} & React.HTMLAttributes<HTMLDivElement>;

export const RecapItem: React.FC<RecapItemProps> = ({ amount, unit, ratio, ...props }) => {
    return (
        <div className={`recap-item ${ratio}`} {...props}>
            <Typography intrinsic="h5">
                {amount}
                {unit}
            </Typography>
        </div>
    );
};
